import { useGetBoardUsers } from 'entities/board/api/hooks/useGetBoardUsers';
import { useGetCurrentBoardId } from 'entities/user/api/hooks/useGetCurrentBoardId';
import { useEffect, useState } from 'react';

export type UseBoardUsersType = {
  /**
   * The ID of the board for which to fetch users.
   * When is not provided, it will default to the current board ID.
   */
  boardId?: string | number;

  /**
   * The number of users to fetch per page.
   */
  perPage?: number;
};

/**
 * Hook to load users of a specific board.
 * @param params - Parameters for fetching board users.
 */
export const useBoardUsers = (params?: UseBoardUsersType) => {
  const [queryParams, setQueryParams] = useState({
    page: 1,
    perPage: params?.perPage || 50,
  });
  const [users, setUsers] = useState<any[]>([]);
  const currentBoardId = useGetCurrentBoardId();

  const boardToCheck = params?.boardId || currentBoardId;

  const { data, isLoading, isInitialLoading } = useGetBoardUsers({
    pathParams: {
      boardId: params?.boardId || currentBoardId!,
    },
    queryParams,
  });

  useEffect(() => {
    // Reset users and query params when boardId changes
    setUsers([]);
    setQueryParams({
      page: 1,
      perPage: params?.perPage || 10,
    });
  }, [boardToCheck, params?.perPage]);

  useEffect(() => {
    if (data?.data) {
      setUsers(s => {
        // Append new users to the existing list
        const newUsers = data.data.filter(
          user => !s.some(existingUser => existingUser.id === user.id),
        );
        return [...s, ...newUsers];
      });

      if (data.meta.currentPage < data.meta.lastPage) {
        setQueryParams(s => ({
          ...s,
          page: s.page + 1, // Increment page for next fetch
        }));
      }
    }
  }, [data]);

  return { data: users, isLoading, isInitialLoading };
};
